import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/layout/PageHeader';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardTitle } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useEffectiveFacilityIds, useIsFacilityLocked } from '@/hooks/useGlobalFacility';
import { usePageContext } from '@/hooks/usePageContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { cn } from '@/lib/utils';
import { useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import * as tasksSvc from '@/services/tasks.service';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import { isSelect } from '@/types/filters.types';

import { firstAppointmentLabel } from './taskRuleLabels';

/** Stored task_groups CSV filter columns used by legacy task matching. */
const TASK_RULE_DIMENSION_KEYS = [
  'planTypeID',
  'doctorID',
  'coordID',
  'nurseID',
  'facilityID',
  'procedureID',
  'leadSourceID',
  'tagID',
  'appointmentTypeID',
  'appointmentTypeGroupID',
  'appointmentFacilityID',
  'currentStatus',
  'languageID',
  'productID',
  'membershipID',
  'packageID',
  'extraFilters',
] as const;

type TaskRuleDimensionKey = (typeof TASK_RULE_DIMENSION_KEYS)[number];

const TASK_RULE_FILTER_LABELS: Record<TaskRuleDimensionKey, string> = {
  planTypeID: 'Plan Type',
  doctorID: 'Doctor',
  coordID: 'Coordinator',
  nurseID: 'Nurse',
  facilityID: 'Facility',
  procedureID: 'Procedure',
  leadSourceID: 'Lead Source',
  tagID: 'Patient Tag',
  appointmentTypeID: 'Appointment Type',
  appointmentTypeGroupID: 'Appointment Type Group',
  appointmentFacilityID: 'Appointment Facility',
  currentStatus: 'Appointment Status',
  languageID: 'Language',
  productID: 'Product',
  membershipID: 'Membership',
  packageID: 'Package',
  extraFilters: 'Extra Filters',
};

/** Dimensions whose label is a staff role the account can rename. */
const TASK_RULE_DIMENSION_ROLE_TYPES: Partial<Record<TaskRuleDimensionKey, string>> = {
  doctorID: 'Doctor',
  coordID: 'Coordinator',
  nurseID: 'Nurse',
};

const BASE_TASK_RULE_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search rules…' },
  { key: 'groupStatus', label: 'Group Status', type: 'select', allLabel: 'All statuses' },
  { key: 'triggerType', label: 'Trigger type', type: 'select', allLabel: 'All types' },
];

const TRIGGER_OPTION_FILTER_KEY = 'triggerOption';

const ALL_TASK_RULE_FILTER_CONFIGS: FilterConfig[] = [
  ...BASE_TASK_RULE_FILTER_CONFIGS,
  { key: TRIGGER_OPTION_FILTER_KEY, label: 'Trigger Option', type: 'select', allLabel: 'All options' },
  ...TASK_RULE_DIMENSION_KEYS.map(
    (key) =>
      ({
        key,
        label: TASK_RULE_FILTER_LABELS[key],
        type: 'select',
        allLabel: 'All',
      }) as FilterConfig,
  ),
];

function valuesText(values: string[]): string {
  return values.length > 0 ? values.join(', ') : 'None';
}

function firstSelect(values: Record<string, FilterValue>, key: string): string {
  const v = values[key];
  return isSelect(v) ? v[0] ?? '' : '';
}

function hasDimensionKey(key: string): key is TaskRuleDimensionKey {
  return (TASK_RULE_DIMENSION_KEYS as readonly string[]).includes(key);
}

function triggerTypeTint(triggerType: number): string {
  switch (triggerType) {
    case 0:
      return 'border-blue-100 bg-blue-50/40';
    case 1:
      return 'border-emerald-100 bg-emerald-50/40';
    case 2:
      return 'border-violet-100 bg-violet-50/40';
    case 3:
      return 'border-amber-100 bg-amber-50/40';
    case 4:
      return 'border-cyan-100 bg-cyan-50/40';
    case 5:
      return 'border-rose-100 bg-rose-50/40';
    default:
      return 'border-slate-100 bg-slate-50/40';
  }
}

function labelForStoredFilterValue(filter: tasksSvc.TaskRuleStoredFilter, value: string): string {
  const index = filter.values.findIndex((item) => String(item) === value);
  return filter.labels[index] || 'Unknown';
}

function triggerOptionValue(rule: tasksSvc.TaskRuleGroup, trigger: tasksSvc.TaskRuleTrigger): string {
  return `${rule.triggerType}:${trigger.id}:${trigger.label}`;
}

function triggerOptionValues(rule: tasksSvc.TaskRuleGroup): string[] {
  return [...rule.assignTriggers, ...rule.removeTriggers].map((trigger) =>
    triggerOptionValue(rule, trigger),
  );
}

function buildSearchHaystack(rule: tasksSvc.TaskRuleGroup): string {
  const parts: string[] = [
    rule.name,
    rule.active ? 'Active' : 'Inactive',
    rule.required ? 'Required' : '',
    rule.applyToPlan ? 'Applies To Plan' : '',
    firstAppointmentLabel(rule) ?? '',
    rule.triggerTypeLabel,
    String(rule.triggerType),
    rule.triggerDetails ?? '',
    ...rule.assignTriggers.map((t) => t.label),
    ...rule.removeTriggers.map((t) => t.label),
    ...rule.filters.flatMap((f) => [f.label, ...f.values, ...f.rawValues]),
    ...Object.values(rule.storedFilters ?? {}).flatMap((f) => [f.rawCsv, ...f.values, ...f.labels]),
  ];

  for (const t of rule.tasks) {
    parts.push(
      t.name,
      t.when,
      t.recurrence,
      t.assignedTo,
      t.priority,
      t.actionTypeLabel,
      t.actionSummary,
      t.notes ?? '',
      String(t.actionType),
    );
  }

  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function matchesDimension(
  rule: tasksSvc.TaskRuleGroup,
  key: TaskRuleDimensionKey,
  selected: string[],
): boolean {
  if (selected.length === 0) {
    return true;
  }

  const entry = rule.storedFilters?.[key];
  if (!entry || entry.isWildcard || entry.values.length === 0) {
    return false;
  }

  const selectedSet = new Set(selected);
  return entry.values.some((value) => selectedSet.has(String(value)));
}

function ruleMatchesSidebar(
  rule: tasksSvc.TaskRuleGroup,
  values: Record<string, FilterValue>,
  searchHaystack: string,
  configuredKeys: readonly TaskRuleDimensionKey[],
): boolean {
  const q = firstSelect(values, 'search').trim().toLowerCase();
  if (q && !searchHaystack.includes(q)) {
    return false;
  }

  const groupStatusSelected = isSelect(values.groupStatus) ? values.groupStatus : [];
  if (groupStatusSelected.length > 0) {
    const status = rule.active ? 'active' : 'inactive';
    if (!groupStatusSelected.includes(status)) {
      return false;
    }
  }

  const triggerSelected = isSelect(values.triggerType) ? values.triggerType : [];
  if (triggerSelected.length > 0) {
    const t = String(rule.triggerType);
    if (!triggerSelected.includes(t)) {
      return false;
    }
  }

  const triggerOptionSelected = isSelect(values[TRIGGER_OPTION_FILTER_KEY])
    ? values[TRIGGER_OPTION_FILTER_KEY]
    : [];
  if (triggerOptionSelected.length > 0) {
    const ruleOptions = new Set(triggerOptionValues(rule));
    if (!triggerOptionSelected.some((option) => ruleOptions.has(option))) {
      return false;
    }
  }

  for (const key of configuredKeys) {
    const sel = isSelect(values[key]) ? values[key] as string[] : [];
    if (!matchesDimension(rule, key, sel)) {
      return false;
    }
  }

  return true;
}

function TriggerList({
  label,
  triggers,
  fallback,
}: {
  label: string;
  triggers: tasksSvc.TaskRuleTrigger[];
  fallback: string;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-800">
        {triggers.length > 0 ? valuesText(triggers.map((trigger) => trigger.label)) : fallback}
      </dd>
    </div>
  );
}

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <Card>
      <CardBody>
        <p className="text-sm text-muted-foreground">{children}</p>
      </CardBody>
    </Card>
  );
}

function TaskRuleCard({ rule }: { rule: tasksSvc.TaskRuleGroup }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const firstAppointment = firstAppointmentLabel(rule);
  const triggerDetails = rule.triggerDetails || rule.triggerTypeLabel;
  const ToggleIcon = isExpanded ? ChevronDown : ChevronRight;

  return (
    <Card noPadding className={cn('overflow-hidden', triggerTypeTint(rule.triggerType))}>
      <button
        type="button"
        className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-white/50"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((value) => !value)}
      >
        <ToggleIcon className="h-4 w-4 flex-shrink-0 text-slate-500" />

        <div className="flex min-w-0 flex-1 items-center gap-2">
          <CardTitle className="truncate text-base">{rule.name}</CardTitle>
          <Badge variant={rule.active ? 'success' : 'neutral'} size="sm">
            {rule.active ? 'Active' : 'Inactive'}
          </Badge>
          {rule.required && (
            <Badge variant="warning" size="sm">
              Required
            </Badge>
          )}
          {rule.applyToPlan && (
            <Badge variant="info" size="sm">
              Applies To Plan
            </Badge>
          )}
          {firstAppointment && (
            <Badge variant="info" size="sm">
              {firstAppointment}
            </Badge>
          )}
        </div>

        <div className="hidden min-w-0 flex-shrink items-center gap-2 text-sm text-slate-600 md:flex">
          <span className="font-medium text-slate-800">{rule.triggerTypeLabel}</span>
          <span className="text-slate-300">|</span>
          <span className="truncate">{triggerDetails}</span>
        </div>
      </button>

      {isExpanded && (
        <CardBody className="space-y-4 border-t border-border p-5">
          <dl className="rounded-lg border border-border bg-slate-50 p-4">
            <TriggerList
              label="Tasks Fire When"
              triggers={rule.assignTriggers}
              fallback={triggerDetails}
            />
            <div className="mt-3">
              <TriggerList
                label="Tasks Are Removed When"
                triggers={rule.removeTriggers}
                fallback="No removal trigger configured"
              />
            </div>
          </dl>

          <div className="rounded-lg border border-border bg-slate-50 p-4">
            <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">Filters</h3>
            {rule.filters.length === 0 ? (
              <p className="mt-1 text-sm text-slate-800">No additional filters configured.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                {rule.filters.map((filter) => (
                  <span
                    key={filter.key}
                    className="max-w-full rounded-lg bg-white px-2.5 py-1.5 text-left text-xs leading-relaxed text-slate-700 ring-1 ring-slate-200"
                  >
                    <span className="font-medium">{filter.label}:</span> {valuesText(filter.values)}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-900">Tasks That Will Fire</h3>
            {rule.tasks.length === 0 ? (
              <p className="mt-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
                No tasks are attached to this group.
              </p>
            ) : (
              <div className="mt-3 divide-y divide-border rounded-lg border border-border">
                {rule.tasks.map((task) => (
                  <div key={task.taskItemID} className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-slate-900">{task.name}</p>
                          <Badge variant={task.active ? 'success' : 'neutral'} size="sm">
                            {task.active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge variant="neutral" size="sm">{task.priority}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{task.actionSummary}</p>
                      </div>
                      <Badge variant="info" size="sm">{task.actionTypeLabel}</Badge>
                    </div>

                    <dl className="mt-3 grid gap-3 text-sm md:grid-cols-3">
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">When</dt>
                        <dd className="mt-1 text-slate-800">{task.when}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Occurrence</dt>
                        <dd className="mt-1 text-slate-800">{task.recurrence}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Assigned To</dt>
                        <dd className="mt-1 text-slate-800">{task.assignedTo}</dd>
                      </div>
                    </dl>

                    {task.notes && (
                      <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                        {task.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardBody>
      )}
    </Card>
  );
}

export default function TaskRulesPage() {
  const navigate = useNavigate();
  const updateAIContext = usePageContext('Task Rules');
  const filters = useLocalDashboardFilters(ALL_TASK_RULE_FILTER_CONFIGS, 'task-rules');
  const localFacilityIds = Array.isArray(filters.values.facilityID)
    ? (filters.values.facilityID as string[])
    : [];
  const effectiveFacilityIds = useEffectiveFacilityIds(localFacilityIds);
  const isFacilityLocked = useIsFacilityLocked();
  const effectiveFilterValues = useMemo<Record<string, FilterValue>>(() => {
    if (!isFacilityLocked) {
      return filters.values;
    }

    return {
      ...filters.values,
      facilityID: effectiveFacilityIds,
    };
  }, [effectiveFacilityIds, filters.values, isFacilityLocked]);

  const {
    data: rules = [],
    isLoading: rulesLoading,
    isError: rulesError,
    refetch,
  } = useQuery({
    queryKey: ['task-rules'],
    queryFn: tasksSvc.listTaskRules,
    staleTime: 60_000,
  });

  const configuredFilterKeys = useMemo(() => {
    const seen = new Set<TaskRuleDimensionKey>();
    for (const rule of rules) {
      for (const filter of Object.values(rule.storedFilters ?? {})) {
        if (hasDimensionKey(filter.key) && !filter.isWildcard && filter.values.length > 0) {
          seen.add(filter.key);
        }
      }
    }
    return TASK_RULE_DIMENSION_KEYS.filter((key) => seen.has(key));
  }, [rules]);

  const triggerOptionOptions = useMemo<FilterOption[]>(() => {
    const options = new Map<string, FilterOption>();

    for (const rule of rules) {
      for (const trigger of [...rule.assignTriggers, ...rule.removeTriggers]) {
        const value = triggerOptionValue(rule, trigger);
        if (options.has(value)) {
          continue;
        }

        options.set(value, {
          value,
          label: `${trigger.label} (${rule.triggerTypeLabel})`,
        });
      }
    }

    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rules]);

  // Three of the rule dimensions name staff roles the account may have
  // renamed. The stored keys are unchanged — only the displayed labels.
  const { roles } = useRoleTitles();
  const dimensionLabel = useCallback((key: TaskRuleDimensionKey) => {
    const roleType = TASK_RULE_DIMENSION_ROLE_TYPES[key];
    if (!roleType) return TASK_RULE_FILTER_LABELS[key];
    return roles.find((r) => r.roleType === roleType)?.roleTitle?.trim() || TASK_RULE_FILTER_LABELS[key];
  }, [roles]);

  const sidebarFilterConfigs = useMemo<FilterConfig[]>(() => [
    ...BASE_TASK_RULE_FILTER_CONFIGS,
    ...(triggerOptionOptions.length > 0
      ? [{
          key: TRIGGER_OPTION_FILTER_KEY,
          label: 'Trigger Option',
          type: 'select' as const,
          allLabel: 'All options',
        }]
      : []),
    ...configuredFilterKeys.map((key) => ({
      key,
      label: dimensionLabel(key),
      type: 'select' as const,
      allLabel: 'All',
    })),
  ], [configuredFilterKeys, triggerOptionOptions, dimensionLabel]);

  const optionsByDimension = useMemo(() => {
    const acc = {} as Record<TaskRuleDimensionKey, Map<string, FilterOption>>;
    for (const k of TASK_RULE_DIMENSION_KEYS) {
      acc[k] = new Map();
    }

    for (const rule of rules) {
      for (const f of Object.values(rule.storedFilters ?? {})) {
        if (!hasDimensionKey(f.key)) {
          continue;
        }
        if (f.isWildcard) {
          continue;
        }
        const key = f.key;
        for (const raw of f.values) {
          const s = String(raw).trim();
          if (s === '' || acc[key].has(s)) {
            continue;
          }
          const label = labelForStoredFilterValue(f, s);
          acc[key].set(s, {
            value: s,
            label: key === 'extraFilters' && label.length > 64 ? `${label.slice(0, 61)}…` : label,
          });
        }
      }
    }

    return acc;
  }, [rules]);

  const filterOptions = useMemo<Record<string, FilterOption[]>>(() => {
    const triggerOptions = new Map<string, FilterOption>();
    for (const rule of rules) {
      const v = String(rule.triggerType);
      if (!triggerOptions.has(v)) {
        triggerOptions.set(v, { value: v, label: rule.triggerTypeLabel || `Type ${v}` });
      }
    }

    const out: Record<string, FilterOption[]> = {
      groupStatus: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
      triggerType: Array.from(triggerOptions.values()),
      [TRIGGER_OPTION_FILTER_KEY]: triggerOptionOptions,
    };

    for (const key of configuredFilterKeys) {
      out[key] = Array.from(optionsByDimension[key]?.values() ?? []);
    }

    return out;
  }, [configuredFilterKeys, optionsByDimension, rules, triggerOptionOptions]);

  const searchHaystacks = useMemo(() => {
    const m = new Map<number, string>();
    for (const rule of rules) {
      m.set(rule.taskGroupID, buildSearchHaystack(rule));
    }
    return m;
  }, [rules]);

  const visibleRules = useMemo(() => {
    return rules.filter((rule) =>
      ruleMatchesSidebar(rule, effectiveFilterValues, searchHaystacks.get(rule.taskGroupID) ?? '', configuredFilterKeys),
    );
  }, [rules, effectiveFilterValues, searchHaystacks, configuredFilterKeys]);

  useEffect(() => {
    const parts = [
      `Task Rules: ${rules.length} task groups loaded`,
    ];
    if (visibleRules.length !== rules.length) {
      parts.push(`${visibleRules.length} visible with current filters`);
    }
    updateAIContext({ dataSummary: parts.join('. ') });
  }, [rules.length, visibleRules.length, updateAIContext]);

  if (rulesLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" label="Loading task rules..." />
      </div>
    );
  }

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={sidebarFilterConfigs}
          values={effectiveFilterValues}
          options={filterOptions}
          onChange={filters.setFilter}
          onClear={filters.clearFilter}
          lockedKeys={isFacilityLocked ? ['facilityID'] : undefined}
          showAIAssistant={false}
          defaultCollapsed={false}
          storageKey="task-rules"
        />
      )}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 space-y-5 p-6">
          <PageHeader
            title="Task Rules"
            actions={(
              <Button variant="secondary" onClick={() => navigate('/tasks')}>
                Back to Tasks
              </Button>
            )}
          />
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-6 pt-0">
          {rulesError ? (
            <Card>
              <CardBody className="flex items-center justify-between gap-4">
                <p className="text-sm text-red-600">Unable to load task rules.</p>
                <Button variant="secondary" onClick={() => void refetch()}>
                  Retry
                </Button>
              </CardBody>
            </Card>
          ) : rules.length === 0 ? (
            <EmptyCard>No task groups are configured for this account.</EmptyCard>
          ) : visibleRules.length === 0 ? (
            <EmptyCard>No task groups match the current filters.</EmptyCard>
          ) : (
            visibleRules.map((rule) => (
              <TaskRuleCard key={rule.taskGroupID} rule={rule} />
            ))
          )}
        </div>
      </div>
    </ListPageLayout>
  );
}
